import { UserAvatar } from '@/components/molecules/UserAvatar';
import { formatRelativeTime, fullDateTime } from '@/lib/date';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ActivityItem } from './Activity';

/** Actor avatar — image when available, else initial fallback. */
function ActivityAvatar({
  actor,
}: {
  actor: { name: string; avatarSrc?: string };
}) {
  if (actor.avatarSrc) {
    return (
      <img
        src={actor.avatarSrc}
        alt={actor.name}
        className="h-7 w-7 shrink-0 rounded-full object-cover ring-1 ring-border"
      />
    );
  }
  return (
    <UserAvatar
      name={actor.name}
      className="h-7 w-7"
      fallbackClassName="text-[11px]"
    />
  );
}

/** A single event row in the `Activity` feed. Links out when `item.href` is set. */
export function ActivityRow({ item, now }: { item: ActivityItem; now: Date }) {
  const rowInner = (
    <div className="flex min-w-0 items-center gap-3">
      {item.actor && <ActivityAvatar actor={item.actor} />}
      {item.icon && (
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
          aria-hidden
        >
          {item.icon}
        </span>
      )}
      <div className="min-w-0 flex-1 truncate text-sm text-foreground">
        {item.label}
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <time
            dateTime={item.timestamp.toISOString()}
            className="shrink-0 cursor-default text-xs tabular-nums text-muted-foreground"
          >
            {formatRelativeTime(item.timestamp, { variant: 'feed', now })}
          </time>
        </TooltipTrigger>
        <TooltipContent>{fullDateTime(item.timestamp)}</TooltipContent>
      </Tooltip>
    </div>
  );

  if (item.href) {
    return (
      <a
        href={item.href}
        className="block rounded-md px-3 py-2.5 transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {rowInner}
      </a>
    );
  }
  return <div className="rounded-md px-3 py-2.5">{rowInner}</div>;
}

/** Skeleton placeholder for a single activity row while loading. */
export function ActivitySkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <Skeleton className="h-7 w-7 rounded-full" />
      <Skeleton className="h-4 flex-1" />
      <Skeleton className="h-3 w-12" />
    </div>
  );
}
